import { useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useConfirm } from '@afosto/app-components-react/ConfirmDialog';
import { useSnackbar } from 'notistack';
import scheduleTask from '@afosto/utils-react/tasks/scheduleTask';
import { orders as ordersApi } from '@afosto/order/api/Orders';
import translations from './translations';

const useCancelOrder = order => {
  const confirm = useConfirm();
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [isCancellingOrder, setIsCancellingOrder] = useState(false);
  const [showForceCancelOrder, setShowForceCancelOrder] = useState(false);
  const [warnings, setWarnings] = useState([]);

  const confirmCancelOrder = async () =>
    confirm({
      title: intl.formatMessage(translations.confirmCancelOrder.title),
      description: intl.formatMessage(translations.confirmCancelOrder.description),
      catchOnCancel: false,
      buttons: [
        {
          label: intl.formatMessage(translations.confirmCancelOrder.buttons.cancel),
          type: 'cancel',
          variant: 'outlined',
          style: {
            marginRight: 'auto',
          },
        },
        {
          label: intl.formatMessage(translations.confirmCancelOrder.buttons.continue),
          response: 'cancelOrder',
          type: 'submit',
          variant: 'contained',
          color: 'primary',
        },
      ],
    });

  const handleCancelOrder = async (force = false) => {
    try {
      setIsCancellingOrder(true);

      const response = await ordersApi.cancelOrder(order?.id, {
        ...(force ? { force: force ? 1 : 0 } : {}),
      });
      const { data } = response ?? {};

      if (!force && !data?.is_accepted) {
        setShowForceCancelOrder(true);
        setWarnings(data?.warnings ?? []);
        return Promise.reject();
      }

      if (force && !data?.is_accepted) {
        throw new Error('Not accepted');
      }

      const taskId = response?.headers?.['x-task-id'];
      await scheduleTask(taskId);

      enqueueSnackbar(intl.formatMessage(translations.feedback.orderCancelled), {
        variant: 'success',
      });

      return Promise.resolve();
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.orderNotCancelled), {
        variant: 'error',
      });
      return Promise.reject(err);
    } finally {
      setIsCancellingOrder(false);
    }
  };

  const cancelOrder = async () => {
    const confirmResponse = await confirmCancelOrder();

    if (confirmResponse !== 'cancelOrder') {
      return Promise.reject();
    }

    return handleCancelOrder();
  };

  const clearForceCancel = () => {
    setShowForceCancelOrder(false);
    setWarnings([]);
  };

  const forceCancelOrder = async () => {
    clearForceCancel();

    return handleCancelOrder(true);
  };

  return {
    cancelOrder,
    clearForceCancel,
    forceCancelOrder,
    isCancellingOrder,
    setShowForceCancelOrder,
    setWarnings,
    showForceCancelOrder,
    warnings,
  };
};

export default useCancelOrder;
